import type { AppMode } from '../core/store';

export interface IdleActionGate {
  request(): void;
  setMode(mode: AppMode): void;
  isPending(): boolean;
}

/**
 * Hold a destructive app action until navigation has fully returned to idle.
 *
 * Service-worker activation and reload are deliberately separate gated
 * actions: activation can finish a moment after it was requested, and the
 * driver may have opened search or started a route during that gap.
 */
export function createIdleActionGate(
  initialMode: AppMode,
  action: () => void | Promise<void>,
): IdleActionGate {
  let mode = initialMode;
  let pending = false;
  let running = false;

  const flush = (): void => {
    if (!pending || running || mode !== 'idle') return;
    pending = false;
    running = true;
    let failed = false;
    void Promise.resolve()
      .then(action)
      .catch((err) => {
        failed = true;
        pending = true;
        console.error('[pwa] deferred update action failed', err);
      })
      .finally(() => {
        running = false;
        // A second request may have arrived while the action was running. Do
        // not spin on a failure; the next update/visibility event retries it.
        if (pending && !failed) flush();
      });
  };

  return {
    request(): void {
      pending = true;
      flush();
    },
    setMode(nextMode: AppMode): void {
      mode = nextMode;
      flush();
    },
    isPending(): boolean {
      return pending;
    },
  };
}
